/**
 * The tab open on the current page, for the pin star beside the page title (PageTitlePin).
 *
 * A tab strip publishes its open tab with <OpenTabName>; the title's star reads it to name the
 * pin "<page> · <tab>" and to leave the query keys of slide panels out of the pinned URL. One page
 * shows one tab strip that matters, so the last strip to publish wins; a strip that unmounts
 * takes its entry with it.
 */
import { useSyncExternalStore } from 'react';

let current = null;
const listeners = new Set();

const notify = () => listeners.forEach((listener) => listener());

/** Publish (`info`) or withdraw (null) strip `id`'s open tab: `{ label, omit }`. */
export const setOpenTab = (id, info) => {
  if (info) {
    current = { id, label: info.label, omit: info.omit || [] };
  } else if (current && current.id === id) {
    current = null;
  } else {
    return;
  }
  notify();
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** `{ label, omit }` of the open tab, or null on a page without one. */
export const useOpenTab = () => useSyncExternalStore(subscribe, () => current, () => null);

/** "<page> · <tab>", or one of them alone when the other is missing or they are the same. */
export const tabPinLabel = (page, tab) => {
  const p = String(page || '').trim();
  const t = String(tab || '').trim();
  if (!p || !t || p === t) return t || p;
  return `${p} · ${t}`;
};

/** pathname + query, less the `omit` keys. */
export const tabPinPath = (pathname, search, omit = []) => {
  const params = new URLSearchParams(search || '');
  omit.forEach((key) => params.delete(key));
  const query = params.toString();
  return `${pathname || '/'}${query ? `?${query}` : ''}`;
};
